import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { AppThunk } from 'app/store';

import { SelectOption } from 'types/clients';
import { Location } from 'types/commons';
import { Filters, SortOrders } from 'types/filters';

import { FIRST_PAGE_INDEX, PAGE_SIZE } from 'constants/uiSettings';

import { selectFilters } from './filtersSelectors';

const initialState: Filters = {
  searchTerm: null,
  country: undefined,
  state: undefined,
  city: undefined,
  hostCountry: undefined,
  location: undefined,
  citizenship: undefined,
  user: undefined,
  busDevId: undefined,
  recruiterIds: undefined,
  status: undefined,
  clientStage: undefined,
  industry: undefined,
  skillset: undefined,
  domesticOverseas: undefined,
  benefits: undefined,
  drugTest: undefined,
  backCheck: undefined,
  tfa: undefined,
  page: FIRST_PAGE_INDEX,
  pageSize: PAGE_SIZE,
  sortField: null,
  order: null,
  education: undefined,
  schedule: undefined,
  languages: undefined,
  clientIds: undefined,
  aceContactIds: undefined,
  days: undefined,
  date: undefined,
  fees: undefined,
  clientContactedCheck: undefined,
  seekerContactedCheck: undefined,
  ambassadorId: undefined,
  screeningCompleted: undefined,
  locations: undefined,
  maxMatched: undefined,
  tags: undefined,
  jobseekerLocationsCountry: undefined,
  jobseekerLocationsState: undefined,
  jobseekerLocationsCity: undefined,
  jobIds: undefined
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    // TODO: clean up
    setSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setCountry(state, action: PayloadAction<string | string[] | undefined>) {
      state.country = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setState(state, action: PayloadAction<string | undefined>) {
      state.state = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setCity(state, action: PayloadAction<string | undefined>) {
      state.city = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setHostCountry(state, action: PayloadAction<string[] | undefined>) {
      state.hostCountry = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setLocation(state, action: PayloadAction<string | undefined>) {
      state.location = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setCitizenship(state, action: PayloadAction<string[]>) {
      state.citizenship = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setUser(state, action: PayloadAction<string[]>) {
      state.user = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setBusDevId(state, action: PayloadAction<string[]>) {
      state.busDevId = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setRecruiterIds(state, action: PayloadAction<string[]>) {
      state.recruiterIds = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setStatus(state, action: PayloadAction<string[]>) {
      state.status = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setClientStage(state, action: PayloadAction<string[]>) {
      state.clientStage = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setIndustry(state, action: PayloadAction<string[]>) {
      state.industry = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setSkillset(state, action: PayloadAction<string[]>) {
      state.skillset = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setDomesticOverseas(state, action: PayloadAction<string[]>) {
      state.domesticOverseas = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setBenefits(state, action: PayloadAction<boolean>) {
      state.benefits = action.payload || undefined;
      state.page = FIRST_PAGE_INDEX;
    },
    setDrugTest(state, action: PayloadAction<boolean>) {
      state.drugTest = action.payload || undefined;
      state.page = FIRST_PAGE_INDEX;
    },
    setBackCheck(state, action: PayloadAction<boolean>) {
      state.backCheck = action.payload || undefined;
      state.page = FIRST_PAGE_INDEX;
    },
    setTfa(state, action: PayloadAction<boolean>) {
      state.tfa = action.payload || undefined;
      state.page = FIRST_PAGE_INDEX;
    },
    setEducation(state, action: PayloadAction<string[]>) {
      state.education = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setSchedule(state, action: PayloadAction<string[]>) {
      state.schedule = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setLanguages(state, action: PayloadAction<string[]>) {
      state.languages = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setClientIds(state, action: PayloadAction<SelectOption[]>) {
      state.clientIds = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setAceContactsIds(state, action: PayloadAction<string[]>) {
      state.aceContactIds = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
    },
    setSortField(state, action: PayloadAction<string | null>) {
      state.sortField = action.payload;
    },
    setSortOrder(state, action: PayloadAction<SortOrders | null>) {
      state.order = action.payload;
    },
    setFilters(state, action: PayloadAction<Partial<Filters>>) {
      Object.keys(action.payload).forEach((key) => {
        const typedKey = key as keyof Filters;
        (state as any)[typedKey] = action.payload[typedKey];
      });
    },
    setDays(state, action: PayloadAction<Partial<Filters>>) {
      state.page = FIRST_PAGE_INDEX;
      Object.assign(state, action.payload);
    },
    setDate(state, action: PayloadAction<string | undefined>) {
      state.date = action.payload;
    },
    setFees(state, action: PayloadAction<string[]>) {
      state.fees = action.payload;
    },
    setClientContactedCheck(state, action: PayloadAction<boolean | undefined>) {
      state.clientContactedCheck = action.payload;
    },
    setSeekerContactedCheck(state, action: PayloadAction<boolean | undefined>) {
      state.seekerContactedCheck = action.payload;
    },
    setAmbassadorId(state, action: PayloadAction<string[]>) {
      state.ambassadorId = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setScreeningCompleted(state, action: PayloadAction<boolean | undefined>) {
      state.screeningCompleted = action.payload;
    },
    setLocations(state, action: PayloadAction<Location[]>) {
      state.locations = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setMaxMatched(state, action: PayloadAction<boolean | undefined>) {
      state.maxMatched = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    // KEEP THIS REDUCERS
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setTags(state, action: PayloadAction<string[] | undefined>) {
      state.tags = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setJobseekerLocationsCountry(
      state,
      action: PayloadAction<string | undefined>
    ) {
      state.jobseekerLocationsCountry = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setJobseekerLocationsState(
      state,
      action: PayloadAction<string | undefined>
    ) {
      state.jobseekerLocationsState = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setJobseekerLocationsCity(
      state,
      action: PayloadAction<string | undefined>
    ) {
      state.jobseekerLocationsCity = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setJobIds(state, action: PayloadAction<string[] | undefined>) {
      state.jobIds = action.payload;
      state.page = FIRST_PAGE_INDEX;
    },
    setClearFilters: (
      state,
      action: PayloadAction<{ preserveFacetFields?: boolean } | undefined>
    ) => ({
      ...initialState,
      ...(action.payload?.preserveFacetFields && {
        facetFields: state.facetFields
      })
    }),
    updateFilterField(
      state,
      action: PayloadAction<{ field: keyof Filters; value: any }>
    ) {
      const { field, value } = action.payload;

      if (value !== undefined) {
        (state as any)[field] = value;
      } else {
        delete (state as any)[field];
      }

      if (field !== 'page') {
        state.page = FIRST_PAGE_INDEX;
      }
    },
    updateMultipleFilters(state, action: PayloadAction<Partial<Filters>>) {
      const updates = action.payload;

      Object.entries(updates).forEach(([key, value]) => {
        const field = key as keyof Filters;
        if (value !== undefined) {
          (state as any)[field] = value;
        }
      });
      state.page = FIRST_PAGE_INDEX;
    }
  }
});

export const {
  setSearchTerm,
  setCountry,
  setState,
  setCity,
  setHostCountry,
  setLocation,
  setCitizenship,
  setUser,
  setBusDevId,
  setRecruiterIds,
  setStatus,
  setClientStage,
  setIndustry,
  setSkillset,
  setDomesticOverseas,
  setBenefits,
  setDrugTest,
  setBackCheck,
  setEducation,
  setSchedule,
  setTfa,
  setLanguages,
  setClientIds,
  setAceContactsIds,
  setPage,
  setPageSize,
  setSortField,
  setSortOrder,
  setFilters,
  setClearFilters,
  setDays,
  setDate,
  setFees,
  setClientContactedCheck,
  setSeekerContactedCheck,
  setAmbassadorId,
  setScreeningCompleted,
  setLocations,
  setMaxMatched,
  updateFilterField,
  updateMultipleFilters,
  setTags,
  setJobseekerLocationsCountry,
  setJobseekerLocationsState,
  setJobseekerLocationsCity,
  setJobIds
} = filtersSlice.actions;

const isObject = (value: unknown): boolean =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const generateParams = (
  filters:
    | Partial<Filters>
    | Record<string, boolean | undefined>
    | Record<string, string>
): string => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (isObject(value)) {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.append(key, value.join(','));
      }
    } else if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });

  return `?${params.toString()}`;
};

export const updateFilters =
  (filters: Partial<Filters>): AppThunk =>
  (dispatch, getState) => {
    const currentFilters = selectFilters(getState());
    dispatch(setFilters({ ...currentFilters, ...filters }));
  };

export default filtersSlice.reducer;
